import { useCallback, useEffect, useRef, useState } from 'react'

type UseSwipeBackProps = {
  direction?: 'ttb' | 'rtl' | 'ltr'
  onClose: () => void
  /** スワイプ確定しきい値（画面幅に対する割合, デフォルト: 0.4） */
  threshold?: number
  /** スワイプバックを有効化するか（未指定時は direction === 'ttb' で判定） */
  enabled?: boolean
}

type SwipeBackState = {
  translateX: number
  isSwiping: boolean
  /** 戻りアニメーション中 */
  isAnimating: boolean
}

/** スワイプバックのアニメーション duration (ms) — CSS transition と同期 */
export const SWIPE_BACK_ANIMATION_MS = 250

/**
 * 右スワイプ（左→右方向）でブラウザバック風に戻るフック。
 * ページが指に追従してスライドし、しきい値を超えたら onClose を呼ぶ。
 * 有効化は enabled プロップで制御し、未指定時は direction === 'ttb' で判定する。
 */
export const useSwipeBack = ({ direction, onClose, threshold = 0.4, enabled: enabledProp }: UseSwipeBackProps) => {
  const [translateX, setTranslateX] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  const enabled = enabledProp ?? direction === 'ttb'

  // 最新の値をrefに保持（イベントリスナー内から安定して参照するため）
  const enabledRef = useRef(enabled)
  const isAnimatingRef = useRef(isAnimating)
  const onCloseRef = useRef(onClose)
  const thresholdRef = useRef(threshold)
  useEffect(() => {
    enabledRef.current = enabled
    isAnimatingRef.current = isAnimating
    onCloseRef.current = onClose
    thresholdRef.current = threshold
  }, [enabled, isAnimating, onClose, threshold])

  // タッチ開始位置
  const startXRef = useRef(0)
  const startYRef = useRef(0)
  // スワイプ方向が確定したか
  const directionLockedRef = useRef<'horizontal' | 'vertical' | null>(null)
  // 現在のtranslateX（stateの遅延なしに参照するため）
  const currentTranslateRef = useRef(0)
  // アクティブなタッチか
  const isActiveRef = useRef(false)
  // DOMノード
  const containerRef = useRef<HTMLDivElement | null>(null)

  // イベントハンドラ（useRefで安定した参照を保持）
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabledRef.current || isAnimatingRef.current) return
    const touch = e.touches[0]
    startXRef.current = touch.clientX
    startYRef.current = touch.clientY
    directionLockedRef.current = null
    isActiveRef.current = true
  }, [])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabledRef.current || !isActiveRef.current || isAnimatingRef.current) return

    const touch = e.touches[0]
    const deltaX = touch.clientX - startXRef.current
    const deltaY = touch.clientY - startYRef.current

    // 方向がまだ確定していない場合、最初の十分な移動で判定
    if (!directionLockedRef.current) {
      const absDeltaX = Math.abs(deltaX)
      const absDeltaY = Math.abs(deltaY)

      // 最低限の移動量（10px）に達していなければ無視
      if (absDeltaX < 10 && absDeltaY < 10) return

      // 水平移動が優勢かつ右方向の場合のみスワイプバック開始
      if (absDeltaX > absDeltaY && deltaX > 0) {
        directionLockedRef.current = 'horizontal'
      } else {
        directionLockedRef.current = 'vertical'
        return
      }
    }

    if (directionLockedRef.current !== 'horizontal') return

    // 右方向のみ追従（左へは動かさない）
    const clampedX = Math.max(0, deltaX)
    currentTranslateRef.current = clampedX
    setTranslateX(clampedX)
    setIsSwiping(true)

    // 縦スクロールを防止（水平スワイプ中のみ）
    if (e.cancelable) {
      e.preventDefault()
    }
  }, [])

  const handleTouchEnd = useCallback(() => {
    if (!enabledRef.current || !isActiveRef.current) return
    isActiveRef.current = false

    if (directionLockedRef.current !== 'horizontal' || currentTranslateRef.current === 0) {
      directionLockedRef.current = null
      return
    }

    const thresholdPx = window.innerWidth * thresholdRef.current
    const didExceedThreshold = currentTranslateRef.current >= thresholdPx

    if (didExceedThreshold) {
      // しきい値超え → 画面外までスライドさせてから閉じる
      setIsAnimating(true)
      setTranslateX(window.innerWidth)
      setTimeout(() => {
        onCloseRef.current()
        setTranslateX(0)
        setIsSwiping(false)
        setIsAnimating(false)
        currentTranslateRef.current = 0
      }, SWIPE_BACK_ANIMATION_MS)
    } else {
      // しきい値未満 → アニメーションで元に戻す
      setIsAnimating(true)
      setTranslateX(0)
      currentTranslateRef.current = 0
      setTimeout(() => {
        setIsSwiping(false)
        setIsAnimating(false)
      }, SWIPE_BACK_ANIMATION_MS)
    }

    directionLockedRef.current = null
  }, [])

  // DOMノードへのリスナー登録（ハンドラはuseCallbackで安定しているため再登録されない）
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('touchstart', handleTouchStart)
        containerRef.current.removeEventListener('touchmove', handleTouchMove)
        containerRef.current.removeEventListener('touchend', handleTouchEnd)
      }

      containerRef.current = node

      if (node) {
        node.addEventListener('touchstart', handleTouchStart, { passive: true })
        node.addEventListener('touchmove', handleTouchMove, { passive: false })
        node.addEventListener('touchend', handleTouchEnd, { passive: true })
      }
    },
    [handleTouchStart, handleTouchMove, handleTouchEnd],
  )

  // cleanup
  useEffect(() => {
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('touchstart', handleTouchStart)
        containerRef.current.removeEventListener('touchmove', handleTouchMove)
        containerRef.current.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd])

  const swipeBackState: SwipeBackState = {
    translateX,
    isSwiping,
    isAnimating,
  }

  return { swipeBackRef: setRef, swipeBackState }
}
